import React from 'react';
import { QRCodeSVG as QRCode } from 'qrcode.react';

/**
 * Retail/FMCG Dense Theme
 * Compact, data-rich, supports batch/expiry/MRP
 */
const RetailDenseTheme = ({ document, settings = {} }) => {
  const primaryColor = settings.theme?.primary_color || '#DC2626';
  const fontFamily = settings.typography?.font_family || 'Arial, sans-serif';
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const numberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    if (num === 0) return 'Zero Rupees Only';
    const crore = Math.floor(num / 10000000);
    const lakh = Math.floor((num % 10000000) / 100000);
    const thousand = Math.floor((num % 100000) / 1000);
    const hundred = Math.floor((num % 1000) / 100);
    const remainder = Math.floor(num % 100);
    let words = '';
    if (crore > 0) words += ones[crore] + ' Crore ';
    if (lakh > 0) words += (lakh < 20 && lakh > 9 ? teens[lakh - 10] : (tens[Math.floor(lakh / 10)] + ' ' + ones[lakh % 10])) + ' Lakh ';
    if (thousand > 0) words += (thousand < 20 && thousand > 9 ? teens[thousand - 10] : (tens[Math.floor(thousand / 10)] + ' ' + ones[thousand % 10])) + ' Thousand ';
    if (hundred > 0) words += ones[hundred] + ' Hundred ';
    if (remainder > 0) {
      if (remainder < 10) words += ones[remainder];
      else if (remainder < 20) words += teens[remainder - 10];
      else words += tens[Math.floor(remainder / 10)] + ' ' + ones[remainder % 10];
    }
    return words.trim() + ' Rupees Only';
  };

  return (
    <div className="print-template retail-dense" style={{ fontFamily, padding: '25px', maxWidth: '210mm', margin: '0 auto', background: '#fff', fontSize: '11px' }}>
      {/* Compact Header with Border */}
      <div style={{ border: `3px solid ${primaryColor}`, padding: '20px', marginBottom: '15px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ flex: 1 }}>
            {settings.branding?.logo_url && (
              <img src={settings.branding.logo_url} alt="Logo" style={{ maxHeight: '50px', marginBottom: '8px' }} />
            )}
            <h1 style={{ fontSize: '20px', fontWeight: '900', color: primaryColor, margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>
              {settings.branding?.company_name || 'STORE NAME'}
            </h1>
            <p style={{ fontSize: '9px', color: '#374151', lineHeight: '1.4', margin: '5px 0 0 0' }}>
              {settings.branding?.address || '123 Main St, City'}
              {settings.branding?.phone && ` • Phone: ${settings.branding.phone}`}
            </p>
            {settings.branding?.gstin && (
              <p style={{ fontSize: '9px', color: '#374151', margin: '2px 0 0 0' }}><strong>GSTIN:</strong> {settings.branding.gstin}</p>
            )}
          </div>
          <div style={{ background: primaryColor, color: '#fff', padding: '12px 18px', borderRadius: '4px', textAlign: 'center', minWidth: '140px' }}>
            <div style={{ fontSize: '9px', fontWeight: '700', letterSpacing: '1.5px', marginBottom: '4px' }}>TAX INVOICE</div>
            <div style={{ fontSize: '16px', fontWeight: '900' }}>{document.quotation_no || 'INV-001'}</div>
            <div style={{ fontSize: '9px', marginTop: '4px' }}>{new Date(document.doc_date || Date.now()).toLocaleDateString('en-IN')}</div>
          </div>
        </div>
      </div>

      {/* Compact Customer Info */}
      <div style={{ display: 'flex', gap: '15px', marginBottom: '15px' }}>
        <div style={{ flex: 1, background: '#F9FAFB', border: '1px solid #D1D5DB', padding: '10px', borderRadius: '3px' }}>
          <div style={{ fontSize: '8px', fontWeight: '700', color: primaryColor, marginBottom: '5px', letterSpacing: '1px' }}>BILL TO</div>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#111827', marginBottom: '3px' }}>{document.party?.name || 'Customer'}</div>
          {document.party?.phone && <div style={{ fontSize: '9px', color: '#6B7280' }}>Ph: {document.party.phone}</div>}
          {settings.toggles?.show_gstin && document.party?.gstin && (
            <div style={{ fontSize: '9px', color: '#6B7280' }}>GSTIN: {document.party.gstin}</div>
          )}
        </div>
        <div style={{ width: '200px', background: '#FEF3C7', border: '1px solid #F59E0B', padding: '10px', borderRadius: '3px' }}>
          <div style={{ fontSize: '8px', fontWeight: '700', color: '#92400E', marginBottom: '5px', letterSpacing: '1px' }}>PAYMENT INFO</div>
          <div style={{ fontSize: '9px', color: '#78350F', lineHeight: '1.5' }}>
            <div><strong>Mode:</strong> {document.payment_mode || 'Cash'}</div>
            <div><strong>Status:</strong> {document.payment_status || 'Paid'}</div>
          </div>
        </div>
      </div>

      {/* Dense Items Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '15px', fontSize: '10px' }}>
        <thead>
          <tr style={{ background: primaryColor, color: '#fff' }}>
            <th style={{ padding: '8px 5px', textAlign: 'left', fontSize: '9px', fontWeight: '700', width: '4%' }}>#</th>
            <th style={{ padding: '8px 5px', textAlign: 'left', fontSize: '9px', fontWeight: '700', width: '30%' }}>ITEM NAME</th>
            {settings.toggles?.show_hsn && <th style={{ padding: '8px 5px', textAlign: 'center', fontSize: '9px', fontWeight: '700', width: '10%' }}>HSN</th>}
            <th style={{ padding: '8px 5px', textAlign: 'center', fontSize: '9px', fontWeight: '700', width: '8%' }}>QTY</th>
            <th style={{ padding: '8px 5px', textAlign: 'right', fontSize: '9px', fontWeight: '700', width: '10%' }}>MRP</th>
            <th style={{ padding: '8px 5px', textAlign: 'right', fontSize: '9px', fontWeight: '700', width: '10%' }}>DISC</th>
            <th style={{ padding: '8px 5px', textAlign: 'right', fontSize: '9px', fontWeight: '700', width: '10%' }}>RATE</th>
            <th style={{ padding: '8px 5px', textAlign: 'center', fontSize: '9px', fontWeight: '700', width: '8%' }}>TAX</th>
            <th style={{ padding: '8px 5px', textAlign: 'right', fontSize: '9px', fontWeight: '700', width: '12%' }}>AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          {(document.items || []).map((item, index) => (
            <React.Fragment key={index}>
              <tr style={{ background: index % 2 === 0 ? '#fff' : '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                <td style={{ padding: '6px 5px', fontSize: '9px', color: '#6B7280', fontWeight: '600' }}>{index + 1}</td>
                <td style={{ padding: '6px 5px', fontSize: '10px', color: '#111827', fontWeight: '600' }}>
                  {item.name}
                  {item.batch && <span style={{ fontSize: '8px', color: '#6B7280', marginLeft: '5px' }}>(Batch: {item.batch})</span>}
                </td>
                {settings.toggles?.show_hsn && <td style={{ padding: '6px 5px', textAlign: 'center', fontSize: '9px', color: '#6B7280' }}>{item.hsn || '-'}</td>}
                <td style={{ padding: '6px 5px', textAlign: 'center', fontSize: '10px', color: '#111827', fontWeight: '600' }}>{item.quantity}</td>
                <td style={{ padding: '6px 5px', textAlign: 'right', fontSize: '9px', color: '#6B7280' }}>{formatCurrency(item.mrp || item.unit_price)}</td>
                <td style={{ padding: '6px 5px', textAlign: 'right', fontSize: '9px', color: '#DC2626', fontWeight: '600' }}>{item.discount || 0}%</td>
                <td style={{ padding: '6px 5px', textAlign: 'right', fontSize: '9px', color: '#111827' }}>{formatCurrency(item.unit_price)}</td>
                <td style={{ padding: '6px 5px', textAlign: 'center', fontSize: '9px', color: '#111827' }}>{item.tax_rate || 0}%</td>
                <td style={{ padding: '6px 5px', textAlign: 'right', fontSize: '10px', color: '#111827', fontWeight: '700' }}>{formatCurrency(item.amount)}</td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: '#F3F4F6', fontWeight: '700' }}>
            <td colSpan={settings.toggles?.show_hsn ? 8 : 7} style={{ padding: '8px 5px', textAlign: 'right', fontSize: '10px', color: '#374151' }}>SUBTOTAL</td>
            <td style={{ padding: '8px 5px', textAlign: 'right', fontSize: '11px', color: '#111827' }}>{formatCurrency(document.totals?.subtotal || 0)}</td>
          </tr>
          <tr style={{ borderBottom: '1px solid #E8E8E8' }}>
            <td colSpan={settings.toggles?.show_hsn ? 8 : 7} style={{ padding: '6px 5px', textAlign: 'right', fontSize: '10px', color: '#6B7280' }}>Discount Total</td>
            <td style={{ padding: '6px 5px', textAlign: 'right', fontSize: '10px', color: '#DC2626', fontWeight: '600' }}>- {formatCurrency(document.totals?.discount_total || 0)}</td>
          </tr>
          <tr style={{ background: '#F9FAFB', fontWeight: '700' }}>
            <td colSpan={settings.toggles?.show_hsn ? 8 : 7} style={{ padding: '8px 5px', textAlign: 'right', fontSize: '10px', color: '#374151' }}>TAXABLE AMOUNT</td>
            <td style={{ padding: '8px 5px', textAlign: 'right', fontSize: '11px', color: '#111827' }}>{formatCurrency(document.totals?.taxable_amount || 0)}</td>
          </tr>
          {settings.toggles?.show_tax_breakup && document.tax_breakup?.map((tax, idx) => (
            <React.Fragment key={idx}>
              <tr style={{ background: '#FAFAFA' }}>
                <td colSpan={settings.toggles?.show_hsn ? 8 : 7} style={{ padding: '6px 5px', textAlign: 'right', fontSize: '9px', color: '#6B7280' }}>CGST @ {tax.rate / 2}%</td>
                <td style={{ padding: '6px 5px', textAlign: 'right', fontSize: '9px', color: '#374151' }}>{formatCurrency(tax.amount / 2)}</td>
              </tr>
              <tr style={{ background: '#FAFAFA' }}>
                <td colSpan={settings.toggles?.show_hsn ? 8 : 7} style={{ padding: '6px 5px', textAlign: 'right', fontSize: '9px', color: '#6B7280' }}>SGST @ {tax.rate / 2}%</td>
                <td style={{ padding: '6px 5px', textAlign: 'right', fontSize: '9px', color: '#374151' }}>{formatCurrency(tax.amount / 2)}</td>
              </tr>
            </React.Fragment>
          ))}
          {document.totals?.round_off !== 0 && (
            <tr style={{ background: '#FAFAFA' }}>
              <td colSpan={settings.toggles?.show_hsn ? 8 : 7} style={{ padding: '6px 5px', textAlign: 'right', fontSize: '9px', color: '#6B7280' }}>Round Off</td>
              <td style={{ padding: '6px 5px', textAlign: 'right', fontSize: '9px', color: '#374151' }}>{document.totals.round_off > 0 ? '+' : ''} {formatCurrency(Math.abs(document.totals.round_off))}</td>
            </tr>
          )}
          <tr style={{ background: primaryColor, color: '#fff', fontWeight: '900' }}>
            <td colSpan={settings.toggles?.show_hsn ? 8 : 7} style={{ padding: '10px 5px', textAlign: 'right', fontSize: '11px', letterSpacing: '1px' }}>GRAND TOTAL</td>
            <td style={{ padding: '10px 5px', textAlign: 'right', fontSize: '14px' }}>{formatCurrency(document.totals?.grand_total || 0)}</td>
          </tr>
          {document.payment?.received_amount && (
            <>
              <tr style={{ background: '#FFF9E6' }}>
                <td colSpan={settings.toggles?.show_hsn ? 8 : 7} style={{ padding: '6px 5px', textAlign: 'right', fontSize: '10px', color: '#78350F' }}>Received</td>
                <td style={{ padding: '6px 5px', textAlign: 'right', fontSize: '10px', color: '#78350F', fontWeight: '600' }}>{formatCurrency(document.payment.received_amount)}</td>
              </tr>
              <tr style={{ background: '#FEF3C7' }}>
                <td colSpan={settings.toggles?.show_hsn ? 8 : 7} style={{ padding: '6px 5px', textAlign: 'right', fontSize: '10px', color: '#92400E', fontWeight: '700' }}>BALANCE DUE</td>
                <td style={{ padding: '6px 5px', textAlign: 'right', fontSize: '11px', color: '#92400E', fontWeight: '700' }}>{formatCurrency(document.totals.grand_total - document.payment.received_amount)}</td>
              </tr>
            </>
          )}
        </tfoot>
      </table>

      {/* Amount in Words */}
      <div style={{ background: '#FFF9E6', border: `2px solid ${primaryColor}`, borderRadius: '4px', padding: '10px 12px', marginBottom: '15px' }}>
        <div style={{ fontSize: '8px', fontWeight: '700', color: primaryColor, marginBottom: '4px', letterSpacing: '1px' }}>AMOUNT IN WORDS</div>
        <div style={{ fontSize: '11px', fontWeight: '700', color: '#111827', textTransform: 'uppercase' }}>
          {numberToWords(document.totals?.grand_total || 0)}
        </div>
      </div>

      {/* Compact Footer */}
      <div style={{ display: 'flex', gap: '15px', fontSize: '9px' }}>
        {document.terms?.notes_public && (
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '700', color: primaryColor, marginBottom: '5px', fontSize: '8px', letterSpacing: '1px' }}>TERMS</div>
            <div style={{ color: '#6B7280', lineHeight: '1.5' }}>{document.terms.notes_public}</div>
          </div>
        )}
        {settings.toggles?.show_bank_details && settings.bank_details && (
          <div style={{ width: '200px', background: '#F9FAFB', border: '1px solid #D1D5DB', borderRadius: '3px', padding: '10px' }}>
            <div style={{ fontSize: '8px', fontWeight: '700', color: primaryColor, marginBottom: '5px', letterSpacing: '1px' }}>BANK DETAILS</div>
            <div style={{ fontSize: '9px', color: '#374151', lineHeight: '1.6' }}>
              <div><strong>Bank:</strong> {settings.bank_details.bank_name}</div>
              <div><strong>A/C:</strong> {settings.bank_details.account_number}</div>
              <div><strong>IFSC:</strong> {settings.bank_details.ifsc}</div>
            </div>
          </div>
        )}
        <div style={{ width: '180px', textAlign: 'center' }}>
          {settings.toggles?.show_signature && (
            <div>
              {settings.qr_code_data && <QRCode value={settings.qr_code_data} size={60} style={{ marginBottom: '5px' }} />}
              <div style={{ borderTop: `2px solid ${primaryColor}`, paddingTop: '5px', marginTop: '10px' }}>
                <div style={{ fontSize: '8px', fontWeight: '700', color: primaryColor }}>AUTH. SIGN</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RetailDenseTheme;